import { getWordForDate } from '@/hooks/useWormbleGame';
import { getAllProgress, formatDateForStorage } from './gameProgress';
import { supabase } from '@/integrations/supabase/client';

export async function cleanupStaleData(userId?: string): Promise<{
  cleaned: boolean;
  localEntriesRemoved: number;
  cloudEntriesRemoved: number;
}> {
  const cleanupFlag = 'wormble-stale-data-cleaned-v1';
  
  // Check if cleanup already ran
  if (localStorage.getItem(cleanupFlag) === 'true') {
    return { cleaned: false, localEntriesRemoved: 0, cloudEntriesRemoved: 0 };
  }

  let localEntriesRemoved = 0;
  let cloudEntriesRemoved = 0;

  // Clean localStorage
  const allProgress = getAllProgress();
  for (const [dateStr, progress] of Object.entries(allProgress)) {
    const date = new Date(dateStr);
    const currentSolution = getWordForDate(date);
    
    if (progress.solution !== currentSolution) {
      localStorage.removeItem(`wormble-progress-${dateStr}`);
      localEntriesRemoved++;
      console.log(`Removed stale local progress for ${dateStr}:`, {
        savedSolution: progress.solution,
        currentSolution
      });
    }
  }

  // Clean cloud data if authenticated
  if (userId) {
    try {
      const { data: cloudProgress } = await supabase
        .from('game_progress')
        .select('*')
        .eq('user_id', userId);

      if (cloudProgress) {
        for (const entry of cloudProgress) {
          const date = new Date(entry.puzzle_date);
          const currentSolution = getWordForDate(date);
          
          if (entry.solution !== currentSolution) {
            await supabase
              .from('game_progress')
              .delete()
              .eq('id', entry.id);
            cloudEntriesRemoved++;
            console.log(`Removed stale cloud progress for ${entry.puzzle_date}:`, {
              savedSolution: entry.solution,
              currentSolution
            });
          }
        }
      }

      // Recalculate cloud stats if we removed cloud entries
      if (cloudEntriesRemoved > 0) {
        // Fetch all remaining valid progress
        const { data: validProgress } = await supabase
          .from('game_progress')
          .select('*')
          .eq('user_id', userId)
          .eq('completed', true);

        if (validProgress) {
          const totalPlayed = validProgress.length;
          const totalWon = validProgress.filter(p => p.won).length;
          
          // Recalculate guess distribution
          const guessDistribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
          validProgress.forEach(p => {
            if (p.won && p.attempts && p.attempts >= 1 && p.attempts <= 5) {
              guessDistribution[p.attempts] = (guessDistribution[p.attempts] || 0) + 1;
            }
          });

          // Calculate streaks
          const sortedProgress = validProgress
            .sort((a, b) => new Date(a.puzzle_date).getTime() - new Date(b.puzzle_date).getTime());
          
          let currentStreak = 0;
          let maxStreak = 0;
          let tempStreak = 0;
          
          sortedProgress.forEach((entry, index) => {
            if (entry.won) {
              tempStreak++;
              maxStreak = Math.max(maxStreak, tempStreak);
              
              // Check if this is part of current streak (ending at today or yesterday)
              const entryDate = new Date(entry.puzzle_date);
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);
              
              if (index === sortedProgress.length - 1) {
                if (formatDateForStorage(entryDate) === formatDateForStorage(today) ||
                    formatDateForStorage(entryDate) === formatDateForStorage(yesterday)) {
                  currentStreak = tempStreak;
                }
              }
            } else {
              tempStreak = 0;
            }
          });

          // Update cloud stats
          await supabase
            .from('game_stats')
            .upsert({
              user_id: userId,
              total_played: totalPlayed,
              total_won: totalWon,
              current_streak: currentStreak,
              max_streak: maxStreak,
              guess_distribution: guessDistribution,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id'
            });
        }
      }
    } catch (error) {
      console.error('Error cleaning cloud data:', error);
    }
  }

  // Mark cleanup as complete
  localStorage.setItem(cleanupFlag, 'true');

  return {
    cleaned: localEntriesRemoved > 0 || cloudEntriesRemoved > 0,
    localEntriesRemoved,
    cloudEntriesRemoved
  };
}
